import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pencil, Loader2, Eye, EyeOff, MousePointerClick, ImageIcon, Code2, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AD_PLACEMENTS, AD_TYPES, placementLabel, typeLabel } from "@/lib/ads";

const BLANK = {
  title: "",
  type: "banner",
  placement: "sidebar",
  image_url: "",
  link_url: "",
  alt: "",
  open_new_tab: true,
  code: "",
  is_active: true,
};

function AdForm({ open, onClose, ad, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const isEdit = !!ad;

  useEffect(() => {
    if (ad) {
      setForm({
        title: ad.title || "",
        type: ad.type || "banner",
        placement: ad.placement || "sidebar",
        image_url: ad.image_url || "",
        link_url: ad.link_url || "",
        alt: ad.alt || "",
        open_new_tab: ad.open_new_tab !== false,
        code: ad.code || "",
        is_active: ad.is_active !== false,
      });
    } else {
      setForm(BLANK);
    }
  }, [ad, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Въведете име на рекламата.");
      return;
    }
    if (form.type === "banner" && !form.image_url.trim()) {
      toast.error("Банерът изисква изображение.");
      return;
    }
    if (form.type === "adsense" && !form.code.trim()) {
      toast.error("AdSense рекламата изисква код.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/ads/${ad.id}`, form);
        toast.success("Рекламата е обновена.");
      } else {
        await api.post("/admin/ads", form);
        toast.success("Рекламата е добавена.");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";
  const labelClass = "mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="ad-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEdit ? "Редактиране на реклама" : "Нова реклама"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className={labelClass}>Име (вътрешно)</label>
            <input className={inputClass} data-testid="ad-title" value={form.title} onChange={set("title")} placeholder="напр. Банер магазин X" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Тип реклама</label>
              <select className={inputClass} data-testid="ad-type" value={form.type} onChange={set("type")}>
                {AD_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Позиция</label>
              <select className={inputClass} data-testid="ad-placement" value={form.placement} onChange={set("placement")}>
                {AD_PLACEMENTS.map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          {form.type === "banner" ? (
            <div className="space-y-4 rounded-lg bg-slate-50 p-4">
              <div>
                <label className={labelClass}>URL на изображението</label>
                <input className={inputClass} data-testid="ad-image-url" value={form.image_url} onChange={set("image_url")} placeholder="https://..." />
              </div>
              <div>
                <label className={labelClass}>Линк при клик</label>
                <input className={inputClass} data-testid="ad-link-url" value={form.link_url} onChange={set("link_url")} placeholder="https://..." />
              </div>
              <div>
                <label className={labelClass}>Alt текст</label>
                <input className={inputClass} data-testid="ad-alt" value={form.alt} onChange={set("alt")} />
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" data-testid="ad-newtab" checked={form.open_new_tab} onChange={toggle("open_new_tab")} className="h-4 w-4 accent-[#0055D4]" />
                Отваряй линка в нов раздел
              </label>
              {form.image_url && (
                <img src={form.image_url} alt="Преглед" className="max-h-40 w-full rounded-lg object-contain" />
              )}
            </div>
          ) : (
            <div className="space-y-2 rounded-lg bg-slate-50 p-4">
              <label className={labelClass}>Google AdSense код</label>
              <textarea
                rows={7}
                className={`${inputClass} font-mono text-xs`}
                data-testid="ad-code"
                value={form.code}
                onChange={set("code")}
                placeholder='<script async src="https://pagead2.googlesyndication.com/..."></script>\n<ins class="adsbygoogle" ...></ins>\n<script>(adsbygoogle = window.adsbygoogle || []).push({});</script>'
              />
              <p className="text-xs text-slate-400">
                Поставете пълния код на рекламната единица от Google AdSense. Кодът се
                изпълнява на живо на сайта.
              </p>
            </div>
          )}

          <label className="flex items-center gap-2 rounded-lg bg-slate-50 p-3 text-sm font-medium text-slate-700">
            <input type="checkbox" data-testid="ad-active" checked={form.is_active} onChange={toggle("is_active")} className="h-4 w-4 accent-[#0055D4]" />
            Активна (показва се на сайта)
          </label>

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              Отказ
            </button>
            <button type="submit" disabled={saving} data-testid="ad-save-btn" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-bold text-white hover:bg-[#0041A8] disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Запази
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdsManager() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: ads, isLoading } = useQuery({
    queryKey: ["admin-ads"],
    queryFn: () => api.get("/admin/ads").then((r) => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-ads"] });
    qc.invalidateQueries({ queryKey: ["ads"] });
  };

  const toggleActive = async (ad) => {
    try {
      await api.put(`/admin/ads/${ad.id}`, { is_active: !ad.is_active });
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (ad) => {
    if (!window.confirm(`Изтриване на реклама „${ad.title}“?`)) return;
    try {
      await api.delete(`/admin/ads/${ad.id}`);
      toast.success("Рекламата е изтрита.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mt-6" data-testid="ads-manager">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Реклами</h2>
          <p className="text-xs text-slate-500">Банери с линк или Google AdSense код по позиции.</p>
        </div>
        <button
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          data-testid="add-ad-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-[#0041A8]"
        >
          <Plus className="h-4 w-4" />
          Нова реклама
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : (ads || []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center text-slate-500">
          Все още няма добавени реклами. Създайте първата с бутона „Нова реклама“.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="divide-y divide-slate-100">
            {ads.map((ad) => (
              <div key={ad.id} className="flex items-center gap-4 p-4" data-testid={`ad-row-${ad.id}`}>
                <div className="flex h-14 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
                  {ad.type === "banner" && ad.image_url ? (
                    <img src={ad.image_url} alt="" className="h-full w-full object-cover" />
                  ) : ad.type === "adsense" ? (
                    <Code2 className="h-6 w-6 text-slate-400" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-slate-400" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                      {typeLabel(ad.type)}
                    </span>
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-primary">
                      {placementLabel(ad.placement)}
                    </span>
                    {!ad.is_active && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-400">
                        Неактивна
                      </span>
                    )}
                  </div>
                  <h3 className="mt-1 truncate font-display text-sm font-bold text-slate-900">{ad.title}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1" data-testid={`ad-impressions-${ad.id}`}>
                      <BarChart3 className="h-3 w-3" />
                      {ad.impressions || 0} импресии
                    </span>
                    <span className="inline-flex items-center gap-1" data-testid={`ad-clicks-${ad.id}`}>
                      <MousePointerClick className="h-3 w-3" />
                      {ad.clicks || 0} {(ad.clicks || 0) === 1 ? "клик" : "клика"}
                    </span>
                    <span className="font-semibold text-slate-600" data-testid={`ad-ctr-${ad.id}`}>
                      CTR {(ad.impressions || 0) > 0 ? ((ad.clicks || 0) / ad.impressions * 100).toFixed(1) : "0.0"}%
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => toggleActive(ad)}
                    data-testid={`ad-toggle-${ad.id}`}
                    className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                    aria-label={ad.is_active ? "Скрий" : "Покажи"}
                    title={ad.is_active ? "Скрий" : "Покажи"}
                  >
                    {ad.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => { setEditing(ad); setDialogOpen(true); }}
                    data-testid={`ad-edit-${ad.id}`}
                    className="rounded-lg p-2 text-slate-500 hover:bg-blue-50 hover:text-primary"
                    aria-label="Редактирай"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => del(ad)}
                    data-testid={`ad-delete-${ad.id}`}
                    className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"
                    aria-label="Изтрий"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AdForm open={dialogOpen} onClose={() => setDialogOpen(false)} ad={editing} onSaved={refresh} />
    </div>
  );
}
